import { Button } from '../../components/ui/button';
import { Link } from 'react-router-dom';
import { Car, Shield, Zap, Clock, CheckCircle, AlertTriangle, DollarSign, Timer } from 'lucide-react';
import { motion } from 'framer-motion';
import rockChip from '../../assets/RockChipSquared.jpg';

export default function WindshieldRepair() {
	return (
		<div>
			<motion.section 
				initial={{ opacity: 0, y: 30 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.8 }}
				className="bg-gradient-to-b from-white to-babyblue/20 border-b gradient-hero"
			>
				<div className="container py-12 md:py-16">
					<div className="grid md:grid-cols-2 gap-12 items-center">
						<motion.div
							initial={{ opacity: 0, x: -30 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.8, delay: 0.2 }}
						>
							<motion.h1 
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.3 }}
								className="text-3xl md:text-4xl font-extrabold text-gradient"
							>
								Windshield Repair
							</motion.h1>
							<motion.p 
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.4 }}
								className="mt-3 max-w-3xl text-gray-600"
							>
								Your windshield is a vital part of your vehicle's structure and safety system. At Ubiquity AutoGlass, we specialize in providing reliable, high-quality windshield repair services designed to restore your windshield's structural integrity and prevent spreading of the damage.
							</motion.p>
							<motion.div 
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.45 }}
								className="mt-4"
							>
								<p className="text-lg font-semibold text-primary">Starting @ $59.99 + $10 per additional repair</p>
								<p className="text-sm text-gray-500">(max 3 per windshield)</p>
							</motion.div>
							<motion.div 
								initial={{ opacity: 0, y: 20 }}
								animate={{ opacity: 1, y: 0 }}
								transition={{ duration: 0.6, delay: 0.5 }}
								className="mt-6 flex gap-3"
							>
								<motion.a 
									href="https://getstarted.ubiquityglass.com" 
									target="_blank" 
									rel="noreferrer"
									whileHover={{ scale: 1.05 }}
									whileTap={{ scale: 0.95 }}
								>
									<Button variant="gradient">Get Started</Button>
								</motion.a>
								<motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
									<Link to="/support"><Button variant="outline">Contact Us</Button></Link>
								</motion.div>
							</motion.div>
						</motion.div>
						<motion.div 
							initial={{ opacity: 0, scale: 0.8 }}
							animate={{ opacity: 1, scale: 1 }}
							transition={{ duration: 0.8, delay: 0.6 }}
							className="flex justify-center"
						>
							<img src={rockChip} alt="Rock chip damage example" className="w-full max-w-lg rounded-lg shadow-lg" />
						</motion.div>
					</div>
				</div>
			</motion.section>

			{/* Repair Process Steps */}
			<motion.section 
				initial={{ opacity: 0, y: 30 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true, amount: 0.3 }}
				transition={{ duration: 0.8 }}
				className="container py-12 md:py-16"
			>
				<motion.h2 
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.6, delay: 0.2 }}
					className="text-2xl md:text-3xl font-bold text-center mb-12"
				>
					Our Windshield Repair Process
				</motion.h2>
				<motion.div 
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.6, delay: 0.3 }}
					className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
				>
					<motion.div 
						initial={{ opacity: 0, scale: 0.8 }}
						whileInView={{ opacity: 1, scale: 1 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.4 }}
						whileHover={{ scale: 1.05 }}
						className="text-center"
					>
						<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
							<Car className="w-8 h-8 text-primary" />
						</div>
						<h3 className="text-lg font-semibold mb-3">Gaining Access</h3>
						<p className="text-gray-600 text-sm">Our technicians carefully probe the chip to ensure the center access point to all legs of the chip are unobstructed. This is done using specialized tools to ensure resin has a way to travel into the chip when put under pressure.</p>
					</motion.div>
					<motion.div 
						initial={{ opacity: 0, scale: 0.8 }}
						whileInView={{ opacity: 1, scale: 1 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.5 }}
						whileHover={{ scale: 1.05 }}
						className="text-center"
					>
						<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
							<Shield className="w-8 h-8 text-primary" />
						</div>
						<h3 className="text-lg font-semibold mb-3">Cleaning Access Point</h3>
						<p className="text-gray-600 text-sm">The damaged area is thoroughly cleaned to remove any debris, dirt, or contaminants that could interfere with the repair process. We use specialized cleaning solutions and tools to ensure the chip or crack is completely clean.</p>
					</motion.div>
					<motion.div 
						initial={{ opacity: 0, scale: 0.8 }}
						whileInView={{ opacity: 1, scale: 1 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.6 }}
						whileHover={{ scale: 1.05 }}
						className="text-center"
					>
						<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
							<Zap className="w-8 h-8 text-primary" />
						</div>
						<h3 className="text-lg font-semibold mb-3">Preparing Injector</h3>
						<p className="text-gray-600 text-sm">Our repair injector is carefully prepared and positioned over the damage. The injector creates a vacuum seal around the chip or crack, ensuring that the repair resin can be properly injected under pressure.</p>
					</motion.div>
					<motion.div 
						initial={{ opacity: 0, scale: 0.8 }}
						whileInView={{ opacity: 1, scale: 1 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.7 }}
						whileHover={{ scale: 1.05 }}
						className="text-center"
					>
						<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
							<CheckCircle className="w-8 h-8 text-primary" />
						</div>
						<h3 className="text-lg font-semibold mb-3">Pressurized Injection Repair</h3>
						<p className="text-gray-600 text-sm">High-quality repair resin is injected under pressure into the damaged area, filling all cracks and voids completely. The resin is then cured using UV light, creating a strong bond that restores the windshield's structural integrity.</p>
					</motion.div>
				</motion.div>
			</motion.section>

			{/* Peace of Mind Section */}
			<motion.section 
				initial={{ opacity: 0, y: 30 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true, amount: 0.3 }}
				transition={{ duration: 0.8 }}
				className="bg-gray-50 py-12 md:py-16"
			>
				<div className="container">
					<motion.h3 
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						className="text-2xl md:text-3xl font-bold text-center mb-8"
					>
						Peace Of Mind
					</motion.h3>
					<div className="max-w-4xl mx-auto text-center">
						<motion.p 
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.6, delay: 0.3 }}
							className="text-gray-600 mb-8"
						>
							We provide a lifetime warranty on all our repair services, ensuring any issues are promptly resolved. To give you further peace of mind, every job and vehicle we service is protected by extensive third-party insurance, offering an added layer of security and trust.
						</motion.p>
						<div className="grid md:grid-cols-3 gap-8">
							<motion.div 
								initial={{ opacity: 0, scale: 0.9 }}
								whileInView={{ opacity: 1, scale: 1 }}
								viewport={{ once: true, amount: 0.3 }}
								transition={{ duration: 0.6, delay: 0.4 }}
								className="text-center"
							>
								<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
									<Shield className="w-8 h-8 text-primary" />
								</div>
								<h4 className="text-lg font-semibold mb-3">Lifetime Warranty</h4>
								<p className="text-gray-600 text-sm">All repairs come with our lifetime warranty protection</p>
							</motion.div>
							<motion.div 
								initial={{ opacity: 0, scale: 0.9 }}
								whileInView={{ opacity: 1, scale: 1 }}
								viewport={{ once: true, amount: 0.3 }}
								transition={{ duration: 0.6, delay: 0.5 }}
								className="text-center"
							>
								<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
									<CheckCircle className="w-8 h-8 text-primary" />
								</div>
								<h4 className="text-lg font-semibold mb-3">Third-Party Insurance</h4>
								<p className="text-gray-600 text-sm">Every job is protected by extensive third-party insurance</p>
							</motion.div>
							<motion.div 
								initial={{ opacity: 0, scale: 0.9 }}
								whileInView={{ opacity: 1, scale: 1 }}
								viewport={{ once: true, amount: 0.3 }}
								transition={{ duration: 0.6, delay: 0.6 }}
								className="text-center"
							>
								<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
									<Zap className="w-8 h-8 text-primary" />
								</div>
								<h4 className="text-lg font-semibold mb-3">Certified Technicians</h4>
								<p className="text-gray-600 text-sm">Rigorous background verification, training, and certification</p>
							</motion.div>
						</div>
					</div>
				</div>
			</motion.section>

			{/* When to Repair */}
			<motion.section 
				initial={{ opacity: 0, y: 30 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true, amount: 0.3 }}
				transition={{ duration: 0.8 }}
				className="container py-12 md:py-16"
			>
				<motion.h3 
					initial={{ opacity: 0, y: 20 }}
					whileInView={{ opacity: 1, y: 0 }}
					viewport={{ once: true, amount: 0.3 }}
					transition={{ duration: 0.6, delay: 0.2 }}
					className="text-2xl md:text-3xl font-bold text-center mb-12"
				>
					When Should You Repair Your Windshield?
				</motion.h3>
				<div className="grid md:grid-cols-2 gap-12">
					<motion.div 
						initial={{ opacity: 0, x: -30 }}
						whileInView={{ opacity: 1, x: 0 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.3 }}
					>
						<h4 className="text-xl font-semibold mb-4 text-green-600">✓ Good for Repair</h4>
						<ul className="space-y-3">
							<li className="flex items-start gap-3">
								<CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
								<span className="text-gray-600">Small chips or cracks smaller than 6 inches</span>
							</li>
							<li className="flex items-start gap-3">
								<CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
								<span className="text-gray-600">Damage that is not in the driver's direct line of sight</span>
							</li>
							<li className="flex items-start gap-3">
								<CheckCircle className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
								<span className="text-gray-600">Cracks or chips that are away from the edges of the windshield</span>
							</li>
						</ul>
					</motion.div>
					<motion.div 
						initial={{ opacity: 0, x: 30 }}
						whileInView={{ opacity: 1, x: 0 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.4 }}
					>
						<h4 className="text-xl font-semibold mb-4 text-red-600">⚠ Needs Replacement</h4>
						<ul className="space-y-3">
							<li className="flex items-start gap-3">
								<AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
								<span className="text-gray-600">Cracks longer than 6 inches or that obstruct your view</span>
							</li>
							<li className="flex items-start gap-3">
								<AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
								<span className="text-gray-600">Damage in the driver's line of sight, impairing visibility</span>
							</li>
							<li className="flex items-start gap-3">
								<AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
								<span className="text-gray-600">Edge cracks that compromise the windshield's structural integrity</span>
							</li>
						</ul>
					</motion.div>
				</div>
			</motion.section>

			{/* Benefits */}
			<motion.section 
				initial={{ opacity: 0, y: 30 }}
				whileInView={{ opacity: 1, y: 0 }}
				viewport={{ once: true, amount: 0.3 }}
				transition={{ duration: 0.8 }}
				className="bg-gray-50 py-12 md:py-16"
			>
				<div className="container">
					<motion.h3 
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.6, delay: 0.2 }}
						className="text-2xl md:text-3xl font-bold text-center mb-12"
					>
						Benefits of Windshield Repair
					</motion.h3>
					<div className="grid md:grid-cols-4 gap-8">
						<motion.div 
							initial={{ opacity: 0, scale: 0.9 }}
							whileInView={{ opacity: 1, scale: 1 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.6, delay: 0.3 }}
							className="text-center"
						>
							<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
								<DollarSign className="w-8 h-8 text-primary" />
							</div>
							<h4 className="text-lg font-semibold mb-3">Cost-Effective</h4>
							<p className="text-gray-600 text-sm">Repairs are less expensive than replacements</p>
						</motion.div>
						<motion.div 
							initial={{ opacity: 0, scale: 0.9 }}
							whileInView={{ opacity: 1, scale: 1 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.6, delay: 0.4 }}
							className="text-center"
						>
							<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
								<Timer className="w-8 h-8 text-primary" />
							</div>
							<h4 className="text-lg font-semibold mb-3">Time-Saving</h4>
							<p className="text-gray-600 text-sm">Most repairs are completed in under 30 minutes</p>
						</motion.div>
						<motion.div 
							initial={{ opacity: 0, scale: 0.9 }}
							whileInView={{ opacity: 1, scale: 1 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.6, delay: 0.5 }}
							className="text-center"
						>
							<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
								<Shield className="w-8 h-8 text-primary" />
							</div>
							<h4 className="text-lg font-semibold mb-3">Safety</h4>
							<p className="text-gray-600 text-sm">Prevents further damage and maintains structural integrity</p>
						</motion.div>
						<motion.div 
							initial={{ opacity: 0, scale: 0.9 }}
							whileInView={{ opacity: 1, scale: 1 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.6, delay: 0.6 }}
							className="text-center"
						>
							<div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
								<CheckCircle className="w-8 h-8 text-primary" />
							</div>
							<h4 className="text-lg font-semibold mb-3">Insurance Coverage</h4>
							<p className="text-gray-600 text-sm">Many insurance providers cover repair costs with no deductible</p>
						</motion.div>
					</div>
				</div>
			</motion.section>

			<section className="animated-gradient particle-container subtle-glow">
				<div className="container py-12 md:py-16 text-white text-center">
					<h3 className="text-2xl font-bold mb-4">Ready to restore your windshield?</h3>
					<p className="mb-4 max-w-2xl mx-auto">Don't let a small crack become a big problem. Ubiquity AutoGlass is here to provide fast, reliable, and affordable windshield repair services.</p>
					<div className="mb-6">
						<p className="text-lg font-semibold text-white">Starting @ $59.99 + $10 per additional repair</p>
						<p className="text-sm opacity-90">(max 3 per windshield)</p>
					</div>
					<div className="flex gap-3 justify-center">
						<a href="https://getstarted.ubiquityglass.com" target="_blank" rel="noreferrer"><Button className="bg-white text-secondary hover:opacity-90">Get Started</Button></a>
						<Link to="/support"><Button variant="outline" className="border-white text-white hover:bg-white/10 bg-transparent">Contact Us</Button></Link>
					</div>
				</div>
			</section>
		</div>
	);
}
